<script setup lang="ts">
    import { computed, ref, watch, provide } from 'vue';
    import { RouterView, useRoute } from 'vue-router';
    import ContactList from '@views/Lists/ContactList.vue';
    import * as contactListApi from '../IssueStatistics.access';
    import type { ContactsResponse } from '../IssueStatistics.access';
    import { getIssueStatisticsContactListHeadings } from './statistics-types';
    import { loadContact } from '@views/Contact/Contact.access';

    const props = defineProps<{
        issueId: string;
        contactType: string;
        dateRange: { start: string | null; end: string | null };
        variationFilter: string | null;
        suppressQueryString: boolean;
        contactFilter?: string;
    }>();

    const route = useRoute();
    const showingContact = computed(() => route.name === 'IssueStatisticsContact');
    const contactListRef = ref<InstanceType<typeof ContactList> | null>(null);
    const lastViewedContactId = ref<string | null>(null);
    const contactWasUpdated = ref(false);

    provide('markContactUpdated', () => {
        contactWasUpdated.value = true;
    });

    watch(
        () => route.params.contactId as string | undefined,
        (id) => {
            if (id) {
                lastViewedContactId.value = id;
                contactWasUpdated.value = false;
            }
        }
    );

    watch(showingContact, async (showing, wasShowing) => {
        if (wasShowing && !showing && lastViewedContactId.value && contactWasUpdated.value) {
            const updated = await loadContact(lastViewedContactId.value);
            contactListRef.value?.updateContact(updated);
        }
    });

    function contactRoute(contactId: string) {
        return { name: 'IssueStatisticsContact', params: { issueId: props.issueId, contactType: props.contactType, contactId }, query: { ...route.query } };
    }

    const headings = getIssueStatisticsContactListHeadings();

    const descriptionMap: Record<string, string> = {
        Sent: $Resources.ContactListDescriptionSent,
        Received: $Resources.ContactListDescriptionReceived,
        Opened: $Resources.ContactListDescriptionOpened,
        OpenClicked: $Resources.ContactListDescriptionOpenClicked,
        Clicked: $Resources.ContactListDescriptionClicked,
        ClickedLink: $Resources.ContactListClickedLinkHeading,
        Bounced: $Resources.ContactListDescriptionBounced,
        Unsubscribed: $Resources.ContactListDescriptionUnsubscribed,
        Shared: $Resources.ContactListDescriptionShared,
        Inactive: $Resources.ContactListDescriptionInactive,
    };

    const typeOrderOptions: Record<string, { key: string; display: string; type?: string }[]> = {
        Opened: [{ key: 'FirstOpen', display: $Resources.ContactsSortByFirstOpenedLabel }],
        Clicked: [{ key: 'FirstClick', display: $Resources.ContactsSortByFirstClickedLabel }],
        OpenClicked: [
            { key: 'FirstOpen', display: $Resources.ContactsSortByFirstOpenedLabel },
            { key: 'FirstClick', display: $Resources.ContactsSortByFirstClickedLabel },
        ],
        ClickedLink: [{ key: 'FirstClick', display: $Resources.ContactsSortByFirstClickedLabel }],
        Unsubscribed: [{ key: 'UnsubscribeReasonValue', display: $Resources.IssueStatisticsUnsubscribeReasonLabel, type: 'unsubscribe-reason' }],
    };

    const title = computed(() => headings[props.contactType] ?? props.contactType);
    const description = computed(() => descriptionMap[props.contactType]);
    const extraOrderOptions = computed(() => typeOrderOptions[props.contactType] ?? []);
    const showOpenClickFilter = computed(() => props.contactType === 'OpenClicked');

    const contextExtra = computed(() => {
        const parts: string[] = [];
        if (props.contactType === 'ClickedLink') {
            const url = route.query.url as string;
            const sqsParam = route.query.suppressQueryString as string;
            if (url) parts.push(`url=${encodeURIComponent(url)}&suppressQueryString=${sqsParam ?? props.suppressQueryString}`);
        }
        if (props.contactType === 'Shared') {
            const channel = route.query.channel as string;
            if (channel) parts.push(`channel=${encodeURIComponent(channel)}`);
        }
        return parts.join('&') || undefined;
    });

    function buildQs(): string {
        return contactListApi.buildQueryString(props.dateRange, props.variationFilter, props.contactFilter || null);
    }

    async function fetchContacts(skip: number, pageSize: number, extra: string): Promise<ContactsResponse> {
        return contactListApi.getContacts(props.issueId, props.contactType, buildQs(), skip, pageSize, extra);
    }

    async function exportExcel(extra: string): Promise<{ Url: string }> {
        return contactListApi.exportContactsExcel(props.issueId, props.contactType, buildQs(), extra);
    }

    async function exportCsv(extra: string): Promise<{ Url: string }> {
        return contactListApi.exportContactsCsv(props.issueId, props.contactType, buildQs(), extra);
    }

    async function exportToNewList(extra: string): Promise<{ ListId: string }> {
        return contactListApi.exportContactsToNewList(props.issueId, props.contactType, buildQs(), extra);
    }

    async function exportToExistingList(extra: string, targetListId: string): Promise<{ ListId: string }> {
        return contactListApi.exportContactsToExistingList(props.issueId, props.contactType, buildQs(), extra, targetListId);
    }

    async function sendExcelByMail(extra: string): Promise<void> {
        return contactListApi.requestExcelExportByMail(props.issueId, props.contactType, buildQs(), extra);
    }

    async function bounceContact(contactId: string): Promise<void> {
        return contactListApi.bounceContact(contactId, props.issueId);
    }

    async function unsubscribeContact(contactId: string): Promise<void> {
        return contactListApi.unsubscribeContact(contactId, props.issueId);
    }
</script>

<template>
    <RouterView v-if="showingContact" />

    <div v-show="!showingContact">
        <ContactList
            ref="contactListRef"
            :key="`${contactType}-${dateRange.start}-${dateRange.end}-${variationFilter}-${contactFilter}`"
            :contactRoute="contactRoute"
            :title="title"
            :description="description"
            :extraOrderOptions="extraOrderOptions"
            :showOpenClickFilter="showOpenClickFilter"
            :contextExtra="contextExtra"
            :hideAddedDateTime="true"
            :fetchContacts="fetchContacts"
            :exportExcel="exportExcel"
            :exportCsv="exportCsv"
            :exportToNewList="exportToNewList"
            :exportToExistingList="exportToExistingList"
            :sendExcelByMail="sendExcelByMail"
            :bounceContactFn="bounceContact"
            :unsubscribeContactFn="unsubscribeContact"
            :showingUnsubscribed="props.contactType === 'Unsubscribed'" />
    </div>
</template>
